/**
 * Financial controls — the shared, read-only evaluation contract (ALC-4520, epic ALC-4519).
 *
 * One evaluation answers "what is the financial condition of control X for this scope?". It is
 * independent of closing tasks, notifications and postings: consumers (closing checklist today,
 * operations later) read the same result and apply their own policy.
 *
 * Interface note: docs/features/continuous-close/06-evaluation-seam.md.
 */

import type {
  FinancialControlAssessmentPublication,
  FinancialControlFindingStatus,
  FinancialControlReviewDecision,
  FinancialControlReviewInvalidationReason,
  PeriodStatus,
} from '../generated/enums.js';
import type { PeriodResponse } from '../gl/periods.js';
import { AP_FINANCIAL_CONTROL_IDS } from './ap.js';
import { AR_FINANCIAL_CONTROL_IDS } from './ar.js';
import { AT_FINANCIAL_CONTROL_IDS } from './at.js';
import { TR_FINANCIAL_CONTROL_IDS } from './tr.js';

export { AP_FINANCIAL_CONTROL_IDS } from './ap.js';
export {
  AR_FINANCIAL_CONTROL_IDS,
  AR03_DIAGNOSTIC_SOURCES,
  type Ar03DiagnosticSource,
} from './ar.js';
export { AT_FINANCIAL_CONTROL_IDS } from './at.js';
export { TR_FINANCIAL_CONTROL_IDS } from './tr.js';

/**
 * Business domains that own controls. Each domain has its own id list, evaluator registry, i18n
 * group and closing-checklist section; adding a domain adds one entry here.
 */
export const FINANCIAL_CONTROL_DOMAINS = ['AP', 'AR', 'TR', 'AT'] as const;
export type FinancialControlDomain = (typeof FINANCIAL_CONTROL_DOMAINS)[number];

/** Minimum scope labels an operator needs to select the same close evaluation coordinate. */
export interface OperationalControlScopeOptions {
  entities: { entityId: string; name: string }[];
  books: { entityId: string; bookId: string; code: string; name: string }[];
  periods: PeriodResponse[];
}

/** AP operations selector labels (ALC-4519). Same shape as every operational surface. */
export type ApOperationalControlScopeOptions = OperationalControlScopeOptions;

/**
 * Stable catalog identities, composed from the per-domain lists. A new domain adds one spread;
 * activating a control never edits this list.
 */
export const FINANCIAL_CONTROL_IDS = [
  ...AP_FINANCIAL_CONTROL_IDS,
  ...AR_FINANCIAL_CONTROL_IDS,
  ...TR_FINANCIAL_CONTROL_IDS,
  ...AT_FINANCIAL_CONTROL_IDS,
] as const;
export type FinancialControlId = (typeof FINANCIAL_CONTROL_IDS)[number];

/**
 * Named missing capability behind an "Em breve" row, prefixed with its control id
 * (e.g. `AP04_RECURRING_OCCURRENCE_EVALUATOR`). Declared in the control's own stub file; its copy
 * lives in that control's web i18n file. No shared list, so activating a control edits nothing here.
 */
export type FinancialControlPrerequisite = `${FinancialControlId}_${string}`;

/** Stable diagnostic codes. Every code has pt-BR and en copy in the `financialControls` namespace. */
export const FINANCIAL_CONTROL_ERROR_CODES = [
  'FINANCIAL_CONTROL_SCOPE_NOT_FOUND',
  'FINANCIAL_CONTROL_EVALUATION_FAILED',
  'FINANCIAL_CONTROL_DIAGNOSTIC_FAILED',
  'FINANCIAL_CONTROL_ASSESSMENT_NOT_FOUND',
  'FINANCIAL_CONTROL_FINDING_NOT_FOUND',
  'FINANCIAL_CONTROL_REVIEW_STALE',
  'FINANCIAL_CONTROL_REVIEW_REQUIRES_FULL_VISIBILITY',
  /** One control's assessment could not be saved; the response names it and the others still save. */
  'FINANCIAL_CONTROL_SAVE_FAILED',
  /** AT05 cannot place the fiscal year end: the entity has no entity-book row for this book. */
  'AT05_ENTITY_BOOK_MISSING',
  /** AT05 cannot place the fiscal year end: the book's entity-book names a calendar that does not exist. */
  'AT05_FISCAL_YEAR_CALENDAR_MISSING',
] as const;
export type FinancialControlErrorCode = (typeof FINANCIAL_CONTROL_ERROR_CODES)[number];

/**
 * Accounting-period scope: explicit tenant, entity, book and period. The cutoff (`effectiveThrough`)
 * and business time zone are resolved from the period and its calendar, never from the request.
 */
export interface FinancialControlPeriodScope {
  kind: 'ACCOUNTING_PERIOD';
  tenantId: string;
  entityId: string;
  bookId: string;
  periodId: string;
  /** First business day of the period (YYYY-MM-DD). */
  periodStart: string;
  /** Inclusive business-date cutoff (YYYY-MM-DD). */
  effectiveThrough: string;
  /** IANA time zone of the period's calendar. */
  timeZone: string;
}

/** "Em breve" is availability, never pass, fail, error, N/A, waiver or accepted exception. */
export type FinancialControlAvailability = 'AVAILABLE' | 'COMING_SOON';
export type FinancialControlExecution = 'SUCCEEDED' | 'FAILED';
export type FinancialControlApplicability = 'APPLICABLE' | 'NOT_APPLICABLE' | 'UNDETERMINED';
export type FinancialControlCoverage = 'COMPLETE' | 'PARTIAL' | 'MISSING' | 'UNKNOWN';
export type FinancialControlVerdict = 'SATISFIED' | 'UNSATISFIED' | 'UNDETERMINED';

/** Exact money total for one currency. `amount` is a decimal string, never a rounded float. */
export interface FinancialControlMoneyTotal {
  currencyCode: string;
  amount: string;
}

/** Source facts that help an operator follow a control finding into its accounting evidence. */
export interface FinancialControlFindingEvidence {
  bookId?: string;
  accounts?: { accountId: string; code: string; name: string }[];
  journalIds?: string[];
  /** How many journals the evidence names in all, when `journalIds` holds only the first ones. */
  journalCount?: number;
  /**
   * A difference finding's sign (ALC-4614, D36). `amount` stays a magnitude so totals add up;
   * this says which side is higher. `signedAmount` is always `bookAmount - expectedAmount`, in
   * the finding's currency, and `direction` is `OVER` when the book holds more than expected.
   */
  difference?: FinancialControlFindingDifference;
  /**
   * AT06 (CFO-06): the right-of-use asset and the book rule a residual-rate refusal applies to,
   * and the refusal code `assetBookCostCents` raised — an operator cannot act on a bare count,
   * only on which asset/book and why it was refused.
   */
  assetCode?: string;
  residualRate?: string;
  refusalCode?: string;
}

export interface FinancialControlFindingDifference {
  bookAmount: string;
  expectedAmount: string;
  signedAmount: string;
  direction: 'OVER' | 'UNDER';
}

export interface FinancialControlMetrics {
  /** Exact number of findings in the disclosed population (not the returned page). */
  count: number;
  /** One total per currency of the disclosed population; empty when `count` is 0. */
  totals: FinancialControlMoneyTotal[];
  /** What `totals` is denominated in. */
  currencyBasis: 'DOCUMENT' | 'FUNCTIONAL';
}

/** Typed link to the domain record whose own command resolves the finding. */
export type FinancialControlResolutionRef =
  | { kind: 'AP_EXPENSE_ACCRUAL'; accrualId: string }
  | { kind: 'AP_BILL'; billId: string }
  | { kind: 'GL_JOURNAL'; journalId: string }
  // ALC-4523 (AP04): a finding with nothing posted yet to resolve to (no bill, no accrual) still
  // links to its recurring template, e.g. an uncovered occurrence or a template data-quality issue.
  | { kind: 'AP_VENDOR_RECURRING_BILL'; templateId: string; vendorId?: string }
  | { kind: 'PURCHASE_ORDER_RECEIPT'; receiptId: string; purchaseOrderId: string }
  | { kind: 'AP_REIMBURSEMENT_REQUEST'; requestId: string }
  // AR (ALC-4534): declared once on the AR base so parallel AR slices share one vocabulary.
  | { kind: 'AR_BILLING_CONTRACT'; contractId: string }
  | { kind: 'AR_REVENUE_SCHEDULE'; contractId: string; scheduleId: string }
  | { kind: 'AR_INVOICE'; invoiceId: string }
  | { kind: 'AR_RECEIPT'; receiptId: string }
  | { kind: 'AR_DEBIT_NOTE'; debitNoteId: string }
  | { kind: 'AR_CUSTOMER'; customerId: string }
  // ALC-4590 (TR01–TR06): an account-level finding resolves in the bank reconciliation of that
  // account and month (statement import, balance proof, statement close); a movement-level one in
  // the review of that movement (reconcile, classify, post). The control never acts itself.
  | { kind: 'BANK_ACCOUNT_RECONCILIATION'; bankAccountId: string; entityId: string; period: string }
  | { kind: 'BANK_TRANSACTION'; transactionId: string; bankAccountId: string }
  // AT (ALC-4607): declared once on the AT base so parallel AT slices share one vocabulary. Each
  // id is the primary key of the Prisma model it names: FixedAsset.assetId,
  // LeaseContract.leaseContractId, IntangibleProject.projectId and ApBillAssetCandidate.candidateId,
  // which also carries the bill it was raised on (billId, the bill header; the model's optional
  // billLineItemId is not part of the reference).
  | { kind: 'FIXED_ASSET'; assetId: string }
  | { kind: 'LEASE_CONTRACT'; leaseContractId: string }
  | { kind: 'INTANGIBLE_PROJECT'; projectId: string }
  | { kind: 'ASSET_CAPITALIZATION_CANDIDATE'; candidateId: string; billId: string };

/**
 * The earlier decision an open finding replaces because the facts it was bound to changed
 * (AP05, ALC-4531: "revisão invalidada"). Live evaluation only; the review record itself, with its
 * invalidation stamp, is in the control's history.
 */
export interface FinancialControlPriorReview {
  decision: FinancialControlReviewDecision;
  /** ISO timestamp the decision was recorded. */
  reviewedAt: string;
  reviewedBy: FinancialControlActor;
  reason: string;
}

export interface FinancialControlFinding {
  /** Deterministic natural key (Health Agent semantics); stable across identical evaluations. */
  findingKey: string;
  reasonCode: string;
  subject: { type: string; id: string };
  amount: FinancialControlMoneyTotal | null;
  /** Business date the condition belongs to (YYYY-MM-DD), e.g. a competência month start. */
  effectiveDate: string | null;
  resolution: FinancialControlResolutionRef | null;
  /** Other records the finding is about, beyond the one `resolution` acts on (ALC-4524). */
  related?: FinancialControlResolutionRef[];
  /** Optional accounting trace displayed with the finding; omitted when the control has none. */
  evidence?: FinancialControlFindingEvidence;
  /**
   * When the condition started (YYYY-MM-DD), only from a real dated source. Lets the screen show an
   * age ("há N dias") without storing a clock-dependent number (D17).
   */
  openedAt?: string;
  /** Pending review requests represented by this live finding (AP_GL05); omitted from history. */
  reviews?: { reviewRequestId: string; requestType: string }[];
  /**
   * An amount shown beside the finding but deliberately NOT part of `amount`, and so never summed
   * into the control's totals (AP05 proposed estimates: preparation backlog, not expense).
   */
  memoAmount?: FinancialControlMoneyTotal;
  /** Set when an earlier decision on this very finding was invalidated by a change of its facts. */
  priorReview?: FinancialControlPriorReview;
  /**
   * Set when a bill linked to the open estimate still awaits settlement (AP05, ALC-4531): the
   * suggestion is for AP06 to confirm or dismiss, so the screen says so and does not offer a new
   * keep meanwhile. Absent (never false) otherwise.
   */
  pendingSettlement?: boolean;
}

/**
 * Viewer disclosure. `viewerRestricted` is set for every payroll-restricted viewer, whether or not
 * anything was hidden, so its presence cannot reveal that confidential records exist.
 */
export interface FinancialControlDisclosure {
  viewerRestricted: boolean;
  /** Saved source sensitivity may have changed since publication; historical evidence is withheld. */
  savedEvidenceWithheld?: boolean;
}

interface FinancialControlResultBase {
  controlId: FinancialControlId;
  /** Owning domain; the closing checklist renders one section per domain. */
  domain: FinancialControlDomain;
}

/**
 * Partial, read-only diagnostic of an "Em breve" control (e.g. findings from the sources it already
 * knows). Same disclosure, metrics, finding keys and source fingerprint as an evaluated control,
 * but deliberately NO verdict and no applicability: a diagnostic can never make the control
 * SATISFIED. The control stays COMING_SOON with its prerequisite.
 */
export interface FinancialControlDiagnosticSucceeded {
  execution: 'SUCCEEDED';
  ruleVersion: string;
  observedAt: string;
  /** Always `PARTIAL` (decision D13): a diagnostic can never claim full coverage. */
  coverage: 'PARTIAL';
  metrics: FinancialControlMetrics;
  disclosure: FinancialControlDisclosure;
  findings: FinancialControlFinding[];
  findingsTruncated: boolean;
  /**
   * What the diagnostic could see but not judge (ALC-4590: TR01 lists the known accounts and
   * their latest statement date). Additive; never a verdict. Omitted when there is nothing.
   */
  observations?: FinancialControlObservation[];
  sourceIdentity: string;
  /**
   * Optional per-feed coverage (e.g. AR03's milestone-evidence and usage feeds), present only
   * when the diagnostic reads more than one source. `ABSENT` means the feed has never produced a
   * row in the official scope, distinct from a `COVERED` feed with zero current gaps.
   */
  sourceCoverage?: FinancialControlDiagnosticSourceCoverage[];
}

export interface FinancialControlDiagnosticSourceCoverage {
  source: string;
  status: 'COVERED' | 'ABSENT';
  throughDate: string | null;
}

export interface FinancialControlDiagnosticFailed {
  execution: 'FAILED';
  ruleVersion: string;
  observedAt: string;
  errorCode: FinancialControlErrorCode;
}

export type FinancialControlDiagnostic =
  FinancialControlDiagnosticSucceeded | FinancialControlDiagnosticFailed;

export interface FinancialControlComingSoonResult extends FinancialControlResultBase {
  availability: 'COMING_SOON';
  prerequisite: FinancialControlPrerequisite;
  /** Present only when the control registers a diagnostic. Never a verdict. */
  diagnostic?: FinancialControlDiagnostic;
}

export interface FinancialControlFailedResult extends FinancialControlResultBase {
  availability: 'AVAILABLE';
  execution: 'FAILED';
  ruleVersion: string;
  observedAt: string;
  errorCode: FinancialControlErrorCode;
  verdict: 'UNDETERMINED';
}

/**
 * Records a control could not judge (not findings; the verdict does not change). Counted for the
 * viewer: a restricted viewer's count leaves out payroll-confidential records.
 */
export interface FinancialControlObservation {
  code: string;
  count: number;
  /** Nonconfidential scope configuration when functional currency is unresolved. */
  functionalCurrencyConfigDetails?: {
    entityId: string;
    bookId: string;
    configuredCurrency: string | null;
    bookCurrencyCode?: string;
    cause: 'MISSING' | 'ENTITY_CURRENCY_MISSING' | 'EMPTY' | 'UNSUPPORTED';
  };
  /** AP07 scope currencies when the book cannot be compared to AP. */
  fxBookDetails?: {
    entityId: string;
    bookId: string;
    apCurrencyCode: string;
    bookCurrencyCode: string;
  };
  /** Source-level AP07 exclusion evidence after payroll disclosure filtering. */
  exclusionDetails?: FinancialControlAp07ExclusionDetail[];
  /** Non-confidential source evidence for AP07 withholding coverage. */
  accountDetails?: {
    accountId: string | null;
    sourceId: string;
    reasonCode: string;
    withholdingKind?: string;
    amount?: string;
    currencyCode?: string;
  }[];
  /** Per-occurrence variance evidence, when the control supports it. */
  details?: FinancialControlObservationDetail[];
  /** Source evidence for AP04 partial coverage, after confidentiality filtering. */
  ap04CoverageGapDetails?: FinancialControlAp04CoverageGapDetail[];
  trueUpDetails?: FinancialControlTrueUpDetail[];
  tieOutSkipDetails?: FinancialControlTieOutSkipDetail[];
  /** Payment-level AP08 partial-coverage evidence, after confidentiality filtering. */
  ap08AwaitingPaymentDetails?: FinancialControlAp08AwaitingPaymentDetail[];
  /** AP09 uncovered bill and every prepaid GL account it affected; saved with the assessment. */
  uncoveredPrepaidDetails?: FinancialControlUncoveredPrepaidDetail[];
  /** AP09 scope-level gap; no prepaid account IDs are inferred from unrelated assets. */
  ap09CoverageDetails?: {
    entityId: string;
    bookId: string;
    periodId: string;
    cutoffDate: string;
    reason: 'NO_PROVISIONED_PREPAID_SOURCE';
  };
  /** GL05 missing-amount bills and their pending review requests. */
  gl05AmountUnavailableDetails?: FinancialControlGl05AmountUnavailableDetail[];
  /** AT01: each bill, line, supplier or project the control refused or skipped, and why. */
  at01RefusalDetails?: FinancialControlAt01RefusalDetail[];
  /**
   * Per-account treasury evidence (ALC-4590). Never carries an account under restricted access
   * (`RESTRICTED_FGA` bank or GL account): those are only counted, for every viewer.
   */
  treasuryAccountDetails?: FinancialControlTreasuryAccountDetail[];
  /**
   * The fixed-asset PIS/COFINS credit positions AT07 judged (ALC-4609), one per asset and tax.
   * `count` on the observation is the total judged; this list may be shorter when the control caps
   * what it returns, so a screen must compare the two before it claims to show everything.
   */
  fixedAssetCreditPositions?: FinancialControlFixedAssetCreditPosition[];
  /** AP11 population accounting saved with the run. */
  ap11PopulationDetails?: {
    analyzedCount: number;
    linkedCount: number;
    unlinkedCount: number;
    cancelledCount: number;
  };
  /** Focus sync evidence when AP11 cannot prove a complete source population at the cutoff. */
  ap11FeedDetails?: {
    entityId: string;
    configId: string | null;
    status: string | null;
    lastSyncAt: string | null;
    cutoffDate: string;
  };
  /** Focus documents whose historical state cannot be reconstructed at the cutoff. */
  ap11HistoricalStateDetails?: {
    documentId: string;
    reason:
      | 'CANCELLATION_DATE_MISSING'
      | 'LINK_STATE_AFTER_CUTOFF_UNKNOWN'
      | 'BILL_STATUS_AT_CUTOFF_UNKNOWN';
  }[];
  /**
   * One row per movement a treasury control left unjudged (ALC-4590): readable evidence of the
   * refusal, never only a count. Movements of restricted-access accounts are counted, not listed.
   */
  treasuryMovementDetails?: FinancialControlTreasuryMovementDetail[];
  /**
   * One row per written-off intangible project AT05 cannot place in any competência because it
   * has no write-off date (CFO-06): readable evidence of the coverage gap, never only a count. Not
   * payroll-confidential — a project's write-off is never a payroll record.
   */
  writeOffUndatedDetails?: FinancialControlAt05WriteOffUndatedDetail[];
  /** AT06 refusal of a book whose functional currency the lease engine cannot measure. */
  at06CurrencyPrecisionDetails?: FinancialControlAt06CurrencyPrecisionDetail;
}

/** A written-off intangible project AT05 could not place in a competência (ALC-4615, CFO-06). */
export interface FinancialControlAt05WriteOffUndatedDetail {
  projectId: string;
  code: string;
  status: 'WRITTEN_OFF';
  writeOffDate: null;
  periodId: string;
  /** The period end AT05 evaluated against (YYYY-MM-DD). */
  cutoffDate: string;
}

/**
 * Why AT06 left a book's lease measurement unjudged (CFO-06): the book, its functional currency
 * and that currency's minor-unit precision against the one the lease engine measures in, and the
 * recognised contracts whose measurement checks were skipped. Leases are not payroll records.
 */
export interface FinancialControlAt06CurrencyPrecisionDetail {
  entityId: string;
  bookId: string;
  bookCode: string;
  functionalCurrency: string;
  /** Decimal places of the functional currency (JPY 0, KWD 3). */
  currencyPrecision: number;
  /** Decimal places the lease engine measures in (2). */
  supportedPrecision: number;
  /** Every recognised contract whose measurement checks were skipped. */
  leaseContractCount: number;
  /** Their codes, sorted; at most 50, so compare with `leaseContractCount`. */
  leaseContractCodes: string[];
  /**
   * The same contracts (same order and cap) with what the book stores for each. Absent on an
   * assessment saved before these were recorded.
   */
  leaseContracts?: FinancialControlAt06UnmeasuredLease[];
  /**
   * Stored liability still owed at the cutoff, over every unmeasured contract (not only the
   * listed ones), one total per contract currency.
   */
  storedLiabilityTotals?: { currencyCode: string; amount: string }[];
  /** Stored right-of-use cost on this book over every unmeasured contract, functional currency. */
  storedRouAssetCostTotal?: string;
  /** The POSTED-book ledger checks (tie-out, unattributed lines) were skipped too. */
  ledgerChecksSkipped: boolean;
}

/**
 * One lease AT06 did not measure, with the figures the book STORES for it. They were written by
 * the cents-only lease engine, so on such a book they are what the ledger holds, not what AT06
 * verified. Amounts are decimal strings exactly as stored, never rounded.
 */
export interface FinancialControlAt06UnmeasuredLease {
  leaseContractId: string;
  contractCode: string;
  /** The contract's currency: the payment, costs and liability below are in it. */
  currencyCode: string;
  /** Instalment as measured at recognition (the snapshot, else the current amount). */
  storedPaymentAmount: string;
  paymentFrequency: string;
  /** Term as measured at recognition (the snapshot, else the current term). */
  termMonths: number;
  /** YYYY-MM-DD. */
  commencementDate: string;
  storedInitialDirectCosts: string;
  storedRestorationProvision: string;
  /** Principal the stored schedule still owes at the cutoff; null when it has no schedule. */
  storedLiabilityAtCutoff: string | null;
  rouAssetCode: string | null;
  /** The right of use's depreciable base on this book, functional currency; null without one. */
  storedRouAssetCost: string | null;
}

/** A bank movement a treasury control could not judge, and why (ALC-4590). */
export interface FinancialControlTreasuryMovementDetail {
  transactionId: string;
  bankAccountId: string;
  /** Posted day (YYYY-MM-DD). */
  postedDate: string;
  /** Absolute amount in the account currency; null when the movement has none. */
  amount: string | null;
  currencyCode: string;
  /**
   * Why it was not judged, e.g. `UNSUPPORTED_TARGET:CARD_STATEMENT`, `NO_RECONCILIATION_GROUP`,
   * `INTERNAL_OPERATION:OTHER`, `TAX_JURISDICTION:US/US_GAAP`.
   */
  reason: string;
}

/** One known bank account as a treasury control saw it at the cutoff (ALC-4590). */
export interface FinancialControlTreasuryAccountDetail {
  bankAccountId: string;
  name: string;
  /** `BankAccountType`: CHECKING, SAVINGS, SALARY, PAYMENT, INVESTMENT, BENEFITS. */
  accountType: string;
  /** `BankAccountStatus` today (ACTIVE, INACTIVE…); evidence decides membership, not status. */
  status: string;
  currencyCode: string;
  /** Latest business day the account has any statement data for, on or before the cutoff. */
  latestStatementDate: string | null;
  /** Statement-balance source at the cutoff; null when no source has a figure. */
  statementSource:
    'USER_DECLARED' | 'STATEMENT_CLOSING' | 'DAILY_BALANCE' | 'LAST_TRANSACTION_BALANCE' | null;
  /** An active Open Finance feed: freshness is measured in business days (daily integration). */
  integrated: boolean;
  /** Countable movements of the period on this account (TR02). */
  movementCount?: number;
  /** Of `movementCount`, how many are reconciled (held by a confirmed or reconciled group). */
  reconciledCount?: number;
  /** Statement for the month closed by the reconciliation (TR03). */
  statementClosed?: boolean;
}

/**
 * One fixed-asset PIS or COFINS credit position as the close evaluation read it (ALC-4609): named
 * by the asset code and the competência it was read at, never by an internal id. The four amounts
 * are the legal credit, in the book's functional currency, as decimal strings with two places.
 * Book depreciation is a different figure and never appears here.
 */
export interface FinancialControlFixedAssetCreditPosition {
  assetCode: string;
  tax: 'PIS' | 'COFINS';
  /** Competência (YYYY-MM) of the period end the position was read at. */
  competence: string;
  currencyCode: string;
  /** The credit the acquisition generated (potential), before any maturation. */
  calculatedAmount: string;
  /** The part of it that matured into a recoverable entitlement up to the period end. */
  appropriatedAmount: string;
  /** Appropriated and not yet offset against an assessment. */
  availableAmount: string;
  /** Appropriated and offset against an assessment. */
  usedAmount: string;
}

export interface FinancialControlAp08AwaitingPaymentDetail {
  paymentId: string;
  billId: string;
  paidDate: string;
  amount: string;
  currencyCode: string;
  status: 'PAID';
}

export interface FinancialControlUncoveredPrepaidDetail {
  billId: string;
  accountIds: string[];
}

/**
 * One thing AT01 could not judge. `cause` says why; the other fields carry the values that
 * triggered it, so an operator can find the record without a query. `subject.id` is the number
 * people know the record by, never an internal id.
 */
export interface FinancialControlAt01RefusalDetail {
  cause:
    | 'FUNCTIONAL_CURRENCY_UNRESOLVED'
    | 'CAPITALIZATION_POLICY_MISSING'
    | 'PROVISION_RATE_MISSING'
    | 'PROVISION_RATE_INVALID'
    | 'SUPPLIER_ADVANCE_OPEN'
    | 'GATE_SUPERSEDED_AFTER_CUTOFF'
    | 'GATE_TEMPLATE_MISSING'
    | 'PROJECTS_OVER_LIMIT'
    | 'QUEUE_OVER_LIMIT';
  subject: { type: string; id: string } | null;
  /** The amount in its own (source) currency, when the refusal is about one. */
  amount: { currencyCode: string; amount: string } | null;
  effectiveDate: string | null;
  /** The first day after the period: what the control judged the record as of. */
  cutoffDate: string;
  /** A rate, framework or similar value that explains the cause. */
  reference: string | null;
}

export interface FinancialControlGl05AmountUnavailableDetail {
  billId: string;
  reviewRequestIds: string[];
}

export type FinancialControlAp04CoverageGapDetail =
  | {
      reason: 'HISTORICAL_TEMPLATE_STATE_UNKNOWN';
      templateId: string;
      currentStatus: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
    }
  | {
      reason: 'BILL_REVERSAL_DATE_UNKNOWN';
      templateId: string;
      billId: string;
      currentBillStatus: string;
      originalJournalId: string;
      originalJournalDate: string;
    };

export interface FinancialControlAp07ExclusionDetail {
  sourceId: string;
  sourceType: 'JOURNAL' | 'BILL' | 'PAYMENT';
  date: string;
  amount: string;
  currencyCode: string;
  status?: string;
  billId?: string;
  billIds?: string[];
  journalIds?: string[];
  accountIds?: string[];
  sourceEntityId?: string;
  currentEntityId?: string;
  reasonCode?: string;
}

export interface FinancialControlTieOutSkipDetail {
  reason:
    | 'MISSING_LINK_AMOUNT'
    | 'MISSING_FOREIGN_ALLOCATION'
    | 'BANK_AMOUNT_MISSING'
    | 'BANK_CURRENCY_MISMATCH'
    | 'MISSING_BANK_ALLOCATION';
  paymentIds: string[];
  billIds: string[];
  bankTransactionId: string | null;
  date: string;
  amount: string | null;
  currencyCode: string | null;
}

export interface FinancialControlTrueUpDetail {
  accrualIds: string[];
  billIds: string[];
  journalIds: string[];
  effectivePeriod: string;
  direction: 'OVER' | 'UNDER';
  estimateAmount: FinancialControlMoneyTotal;
  actualAmount: FinancialControlMoneyTotal;
  differenceAmount: FinancialControlMoneyTotal;
}

export interface FinancialControlObservationDetail {
  templateId: string;
  billIds: string[];
  accrualId: string | null;
  competencyMonth: string;
  direction: 'OVER' | 'UNDER';
  expectedAmount: FinancialControlMoneyTotal;
  actualAmount: FinancialControlMoneyTotal;
  deltaAmount: FinancialControlMoneyTotal;
}

export interface FinancialControlEvaluatedResult extends FinancialControlResultBase {
  availability: 'AVAILABLE';
  execution: 'SUCCEEDED';
  ruleVersion: string;
  observedAt: string;
  applicability: FinancialControlApplicability;
  coverage: FinancialControlCoverage;
  /** Decided on the official (full authorized business scope) population, before disclosure. */
  verdict: FinancialControlVerdict;
  /** Disclosed metrics: official for an unrestricted viewer, visible-only for a restricted one. */
  metrics: FinancialControlMetrics;
  disclosure: FinancialControlDisclosure;
  /** First page of disclosed findings, in a deterministic order. */
  findings: FinancialControlFinding[];
  findingsTruncated: boolean;
  /** Present only when the control left records unjudged (e.g. AP08 payments awaiting reconciliation). */
  observations?: FinancialControlObservation[];
  /**
   * Fingerprint of every source row in the official (full authorized business) scope, computed
   * before viewer disclosure: equal fingerprints mean unchanged sources for every viewer.
   */
  sourceIdentity: string;
}

export type FinancialControlResult =
  FinancialControlComingSoonResult | FinancialControlFailedResult | FinancialControlEvaluatedResult;

export interface FinancialControlEvaluationResponse {
  scope: FinancialControlPeriodScope;
  observedAt: string;
  controls: FinancialControlResult[];
}

export interface FinancialControlEvaluationQuery {
  periodId: string;
  entityId: string;
  bookId: string;
  /** Comma-separated subset; all registered controls when omitted. */
  controlIds?: string;
  /** Only the controls of this domain; all domains when omitted. */
  domain?: FinancialControlDomain;
}

// ---------------------------------------------------------------------------------------------
// Saved assessments, finding history and version-bound reviews (ALC-4522).
// ---------------------------------------------------------------------------------------------

/** Who saved an assessment or recorded a review. */
export interface FinancialControlActor {
  userId: string;
  name: string;
}

/** Body of the explicit "save assessment" command; the server evaluates, the client sends no result. */
export interface FinancialControlSaveAssessmentsRequest {
  periodId: string;
  entityId: string;
  bookId: string;
  /** Comma-separated subset; every available control of the domain/registry when omitted. */
  controlIds?: string;
  domain?: FinancialControlDomain;
}

/**
 * What saving one control's evaluation did. `publication` says whether it became the current
 * assessment (`PUBLISHED`), lost to a newer one already saved (`REJECTED_STALE`, kept as history),
 * or was a failed run that never moves the pointer (`NOT_PUBLISHED`).
 */
export interface FinancialControlSavedAssessment {
  controlId: FinancialControlId;
  domain: FinancialControlDomain;
  assessmentId: string;
  execution: FinancialControlExecution;
  publication: FinancialControlAssessmentPublication;
  ruleVersion: string;
  observedAt: string;
  errorCode?: FinancialControlErrorCode;
}

/**
 * A control whose save failed. Each control is saved in its own transaction, so a failure leaves
 * nothing of that control behind while the controls listed in `assessments` are saved. No database
 * detail is returned; the cause is in the server log.
 */
export interface FinancialControlNotSavedControl {
  controlId: FinancialControlId;
  domain: FinancialControlDomain;
  errorCode: 'FINANCIAL_CONTROL_SAVE_FAILED';
}

export interface FinancialControlSaveAssessmentsResponse {
  scope: FinancialControlPeriodScope;
  observedAt: string;
  /** One row per control that was saved; "Em breve" controls have nothing to save and are omitted. */
  assessments: FinancialControlSavedAssessment[];
  /** Controls whose save failed, by name; empty when every attempted control was saved. */
  notSaved: FinancialControlNotSavedControl[];
}

/** One saved assessment as the history shows it (immutable). Metrics are the viewer's disclosure. */
export interface FinancialControlAssessmentRecord {
  assessmentId: string;
  /** True for the single assessment the current pointer holds. */
  isCurrent: boolean;
  execution: FinancialControlExecution;
  publication: FinancialControlAssessmentPublication;
  ruleVersion: string;
  /** Status of the accounting period when this assessment was saved ("salva com o período fechado"). */
  periodStatus: PeriodStatus;
  observedAt: string;
  savedAt: string;
  savedBy: FinancialControlActor | null;
  /** Set only on a failed run. */
  errorCode: FinancialControlErrorCode | null;
  /** Null on a failed run, which has no result. */
  result: {
    applicability: FinancialControlApplicability;
    coverage: FinancialControlCoverage;
    verdict: FinancialControlVerdict;
    metrics: FinancialControlMetrics;
    /** Immutable, viewer-disclosed coverage evidence captured when this assessment was saved. */
    observations?: FinancialControlObservation[];
    sourceIdentity: string;
  } | null;
}

/** State of one finding across assessments: stable identity, first/last seen, resolution. */
export interface FinancialControlFindingRecord {
  findingId: string;
  findingKey: string;
  status: FinancialControlFindingStatus;
  reasonCode: string;
  subject: { type: string; id: string };
  amount: FinancialControlMoneyTotal | null;
  effectiveDate: string | null;
  resolution: FinancialControlResolutionRef | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  reopenCount: number;
}

/**
 * One finding as a saved assessment saw it. Insert-only: later saves refresh and resolve the live
 * finding, never this record, so an old assessment always shows what it actually showed.
 */
export interface FinancialControlAssessmentFindingRecord {
  /**
   * Identity of the live finding this belongs to (for reviews and the finding history). Null only
   * when a stale-rejected save saw a finding that no published save ever created.
   */
  findingId: string | null;
  findingKey: string;
  reasonCode: string;
  subject: { type: string; id: string };
  amount: FinancialControlMoneyTotal | null;
  effectiveDate: string | null;
  resolution: FinancialControlResolutionRef | null;
  /** Version of the source record the control read; null when the control records none. */
  sourceVersion: string | null;
}

/** Detail of one saved assessment: its metrics and the findings it saw, as saved. */
export interface FinancialControlAssessmentDetail {
  scope: FinancialControlPeriodScope;
  controlId: FinancialControlId;
  assessment: FinancialControlAssessmentRecord;
  /** The disclosed findings, in a deterministic order. */
  findings: FinancialControlAssessmentFindingRecord[];
  findingsTruncated: boolean;
  disclosure: FinancialControlDisclosure;
}

export interface FinancialControlAssessmentDetailParams {
  controlId: FinancialControlId;
  assessmentId: string;
}

/**
 * A person's decision, bound to the assessment, rule version and source version it saw.
 * `validity` is `INVALIDATED` ("revisão invalidada") once a newer published assessment showed a
 * relevant source or rule change; the review itself is never edited or removed.
 */
export interface FinancialControlReviewRecord {
  reviewId: string;
  assessmentId: string;
  /** Null: the whole control. Set: one finding. */
  findingId: string | null;
  decision: FinancialControlReviewDecision;
  reason: string;
  evidence: string | null;
  ruleVersion: string;
  reviewedBy: FinancialControlActor;
  reviewedAt: string;
  validity: 'VALID' | 'INVALIDATED';
  invalidatedAt: string | null;
  invalidatedByAssessmentId: string | null;
  invalidationReason: FinancialControlReviewInvalidationReason | null;
}

export interface FinancialControlHistory {
  scope: FinancialControlPeriodScope;
  controlId: FinancialControlId;
  /** Assessment the current pointer holds; null before the first successful save. */
  currentAssessmentId: string | null;
  /** Newest first. */
  assessments: FinancialControlAssessmentRecord[];
  assessmentsTruncated: boolean;
  /**
   * Open findings first, then closed ones, newest activity first. `SUPERSEDED` means the rule
   * version changed and the new rule no longer flags it ("fora da regra atual"): it says nothing
   * about whether the underlying condition was fixed, unlike `RESOLVED`.
   */
  findings: FinancialControlFindingRecord[];
  findingsTruncated: boolean;
  /** Newest first. */
  reviews: FinancialControlReviewRecord[];
  reviewsTruncated: boolean;
  disclosure: FinancialControlDisclosure;
}

export interface FinancialControlHistoryQuery {
  periodId: string;
  entityId: string;
  bookId: string;
}

export interface FinancialControlRecordReviewRequest {
  periodId: string;
  entityId: string;
  bookId: string;
  /** The assessment the reviewer looked at; must still be the current one. */
  assessmentId: string;
  /** Review one finding of that assessment; omit to review the whole control. */
  findingId?: string;
  decision: FinancialControlReviewDecision;
  reason: string;
  evidence?: string;
}

/** Receipt of a recorded review: the bound versions are what later invalidation compares against. */
export interface FinancialControlRecordedReview {
  reviewId: string;
  assessmentId: string;
  findingId: string | null;
  decision: FinancialControlReviewDecision;
  ruleVersion: string;
  reviewedAt: string;
}
